import React, { type ReactNode, type Ref } from 'react'
import clsx from 'clsx'
import type { FieldErrors } from 'react-hook-form'
import { useFormField } from '@hooks/useFormField'
import Icon, { type IconTypes } from '@components/utils/Icon'

export type BaseFormFieldProps = {
  name: string
  label?: string // TODO: Make this requred once we update all TextInput components to use the label prop
  errors?: FieldErrors
  required?: boolean
  disabled?: boolean
  placeholder?: string
  value?: string
  defaultValue?: string
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  dataCompId?: string
}

export type TextInputProps = BaseFormFieldProps & {
  type?: string
  min?: string | number
  max?: string | number
  maxLength?: number
  minLength?: number
  pattern?: string
  autoFocus?: boolean
  hideLabel?: boolean
  leadingIcon?: IconTypes
  leadingContent?: ReactNode
  description?: string
  inputWrapperClassName?: string
  displayName?: string // TODO: Remove this once we update all TextInput components to use the label prop
  onChange: React.ChangeEventHandler<HTMLInputElement>
  ref?: Ref<HTMLInputElement>
}

const LeadingIcon = ({ type }: { type: IconTypes }) => (
  <span className="tw:pr-2">
    <Icon width={20} height={20} type={type} />
  </span>
)

const TextInput = function TextInput(props: TextInputProps) {
  const {
    name,
    label,
    displayName,
    description,
    required,
    type,
    errors,
    hideLabel,
    leadingIcon,
    leadingContent,
    dataCompId,
    inputWrapperClassName,
    ref,
    ...inputProps
  } = props

  const labelText = displayName || label

  const { fieldError, errorId, descriptionId, ariaDescribedBy, requiredText, ariaInvalid } = useFormField(
    name,
    errors,
    required,
    description
  )

  return (
    <div
      className="tw:flex tw:flex-col tw:justify-center tw:min-w-0 tw:w-full"
      {...(dataCompId && { 'data-compid': dataCompId })}
    >
      <label
        htmlFor={name}
        className={clsx(
          'tw:mb-0 tw:pb-1 tw:text-sm tw:text-gray-700 tw:font-medium',
          hideLabel && 'tw:sr-only',
          fieldError && 'tw:text-red-600'
        )}
      >
        {labelText} {requiredText}
      </label>

      <div
        className={clsx(
          'tw:flex tw:items-center',
          'tw:px-3 tw:py-2',
          'tw:bg-white',
          'tw:border tw:rounded-lg tw:border-gray-400',
          'tw:focus-within:border-primary-400 tw:focus-within:ring-4 tw:ring-primary-100',
          'tw:placeholder-gray-500 tw:placeholder:font-normal',
          fieldError && 'tw:border-red-400 tw:focus-within:border-red-500 tw:focus-within:ring-red-200',
          inputProps.disabled && 'tw:bg-gray-100 tw:opacity-60',
          inputWrapperClassName
        )}
      >
        {!!leadingIcon && <LeadingIcon type={leadingIcon} />}
        {leadingContent && <span className="tw:flex tw:pr-2">{leadingContent}</span>}
        <input
          type={type ?? 'text'}
          id={name}
          name={name}
          aria-required={required}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          className={clsx(
            'tw:w-full tw:font-normal tw:text-base',
            'tw:placeholder-gray-500 tw:placeholder:font-normal',
            'tw:focus:outline-hidden',
            props.disabled && 'tw:cursor-not-allowed'
          )}
          ref={ref}
          {...inputProps}
        />
      </div>

      {description && (
        <p
          id={descriptionId}
          className={clsx('tw:m-0 tw:pt-1 tw:text-sm tw:text-gray-600', fieldError && 'tw:text-red-600')}
        >
          {description}
        </p>
      )}

      <div role="status" aria-relevant="all">
        {fieldError && (
          <p id={errorId} className="tw:text-red-600 tw:font-light tw:text-sm tw:pt-1">
            {fieldError.message}
          </p>
        )}
      </div>
    </div>
  )
}

export default TextInput
